import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Button,
  DatePicker,
  Empty,
  Input,
  Segmented,
  Select,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import {
  ArrowRightOutlined,
  DownOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { formatFinanceCompact as formatCompactPLN, formatFinanceGrouped as formatCurrencyGrouped, formatFinancePercent as formatLocalizedPercent } from '../pendingInvoiceCost';
import { localeForLanguage } from '../../../utils/numberFormatting';
import { useFeedback } from '../../../utils/feedback';
import {
  fetchCompanyBalance,
  type CompanyBalanceMonth,
  type CompanyBalanceOrder,
  type CompanyBalanceResponse,
} from './companyBalanceApi';
import {
  resolveCompanyBalanceRange,
  type CompanyBalancePeriod,
} from './companyBalancePeriod';
import { resolveOrderRealization } from './companyBalanceRealization';
import {
  buildCompanyBalanceOrdersCsv,
  companyBalanceOrdersFilename,
} from './companyBalanceCsv';
import {
  sortCompanyBalanceOrders,
  type CompanyBalanceOrderSort,
  type CompanyBalanceOrderSortKey,
} from './companyBalanceOrderSort';
import './CompanyBalanceWorkspace.css';

function monthLabel(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat(localeForLanguage(language), {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

function periodLabel(anchor: Dayjs, period: CompanyBalancePeriod, language: string): string {
  const locale = localeForLanguage(language);
  if (period === 'MONTH') {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
      .format(anchor.toDate());
  }
  if (period === 'QUARTER') return `Q${Math.floor(anchor.month() / 3) + 1} ${anchor.year()}`;
  if (period === 'HALF_YEAR') return `${anchor.month() < 6 ? 'I' : 'II'} półrocze ${anchor.year()}`;
  return String(anchor.year());
}

function dateLabel(value: string | null, language: string): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return new Intl.DateTimeFormat(localeForLanguage(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsed.toDate());
}

type CompanyBalanceChartMetric = 'REVENUE' | 'COSTS' | 'MARGIN';

function chartMetricValues(month: CompanyBalanceMonth, metric: CompanyBalanceChartMetric) {
  if (metric === 'COSTS') {
    return { recognized: month.orderCosts, planned: month.planned.totalCost };
  }
  if (metric === 'MARGIN') {
    return { recognized: month.orderMargin, planned: month.planned.margin };
  }
  return { recognized: month.revenue, planned: month.planned.revenue };
}

const OrderRealizationValue: React.FC<{
  order: CompanyBalanceOrder;
  label: string;
}> = ({ order, label }) => {
  const realization = resolveOrderRealization(order.evidence);
  const tone = realization.percent >= 100
    ? 'is-complete'
    : realization.percent >= 70
      ? 'is-advanced'
      : realization.percent >= 35
        ? 'is-progress'
        : 'is-start';
  return (
    <span
      className={`company-balance-realization-value ${tone}`}
      aria-label={realization.total > 0 ? `${label} ${realization.percent}%` : `${label}: —`}
    >
      <strong>{realization.total > 0 ? `${realization.percent}%` : '—'}</strong>
      {realization.total > 0 && (
        <span className="company-balance-realization-track" aria-hidden="true">
          <span style={{ width: `${realization.percent}%` }} />
        </span>
      )}
    </span>
  );
};

const FinancialColumnTitle: React.FC<{ label: string; basis: string }> = ({ label, basis }) => (
  <span className="company-balance-financial-heading">
    <strong>{label}</strong>
    <small>{basis}</small>
  </span>
);

const FinancialPair: React.FC<{
  recognized: number | null;
  forecast: number | null;
  recognizedLabel: string;
  forecastLabel: string;
  language: string;
  recognizedPercent?: number | null;
  forecastPercent?: number | null;
  highlight?: boolean;
}> = ({
  recognized,
  forecast,
  recognizedLabel,
  forecastLabel,
  language,
  recognizedPercent,
  forecastPercent,
  highlight = false,
}) => {
  const recognizedText = formatCurrencyGrouped(recognized, 'PLN');
  const forecastText = formatCurrencyGrouped(forecast, 'PLN');
  return (
    <span className={`company-balance-financial-pair ${highlight ? 'is-margin' : ''}`}>
      <strong
        className={recognized != null && recognized < 0 ? 'is-negative' : ''}
        aria-label={`${recognizedLabel}: ${recognizedText}`}
        title={`${recognizedLabel}: ${recognizedText}`}
      >
        {recognizedText}
        {recognizedPercent !== undefined && (
          <i>{formatLocalizedPercent(recognizedPercent, language)}</i>
        )}
      </strong>
      <small
        className={forecast != null && forecast < 0 ? 'is-negative' : ''}
        aria-label={`${forecastLabel}: ${forecastText}`}
        title={`${forecastLabel}: ${forecastText}`}
      >
        {forecastText}
        {forecastPercent !== undefined && (
          <i>{formatLocalizedPercent(forecastPercent, language)}</i>
        )}
      </small>
    </span>
  );
};

const nullableSum = (left: number | null, right: number | null) => left == null || right == null ? null : left + right;

const CompanyBalanceWorkspace: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const feedback = useFeedback();
  const language = i18n.language || 'pl';
  const [anchor, setAnchor] = useState<Dayjs>(dayjs());
  const [period, setPeriod] = useState<CompanyBalancePeriod>('YEAR');
  const [data, setData] = useState<CompanyBalanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [chartMetric, setChartMetric] = useState<CompanyBalanceChartMetric>('REVENUE');
  const [orderSort, setOrderSort] = useState<CompanyBalanceOrderSort>({
    key: 'DATE',
    direction: 'descend',
  });

  const range = useMemo(() => resolveCompanyBalanceRange(anchor, period), [anchor, period]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setLoading(true);
    fetchCompanyBalance(range.from, range.to)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('reports:companyBalance.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [feedback, range.from, range.to, t]);

  const scopedOrders = useMemo(() => {
    if (!data) return [];
    return data.orders.filter((order) => {
      const plannedDate = order.plannedDate ? dayjs(order.plannedDate) : null;
      const plannedInRange = plannedDate?.isValid()
        ? !plannedDate.isBefore(range.start, 'day') && !plannedDate.isAfter(range.end, 'day')
        : false;
      return order.periodSettlementEntryCount > 0 || plannedInRange;
    });
  }, [data, range.end, range.start]);

  const availableStatuses = useMemo(() => (
    Array.from(new Set(scopedOrders.map((order) => order.status).filter(Boolean))).sort()
  ), [scopedOrders]);

  const visibleOrders = useMemo(() => {
    const filtered = scopedOrders.filter((order) => {
      if (statusFilter && order.status !== statusFilter) return false;
      const query = search.trim().toLocaleLowerCase(language);
      if (query && !`${order.displayOrderNumber} ${order.customer?.name || ''}`.toLocaleLowerCase(language).includes(query)) return false;
      return true;
    });
    return sortCompanyBalanceOrders(filtered, orderSort);
  }, [language, orderSort, scopedOrders, search, statusFilter]);

  const downloadVisibleOrders = () => {
    if (visibleOrders.length === 0) return;

    const csv = buildCompanyBalanceOrdersCsv(visibleOrders, language, {
      headers: {
        orderNumber: t('reports:companyBalance.export.headers.orderNumber'),
        customer: t('reports:companyBalance.export.headers.customer'),
        orderStatus: t('reports:companyBalance.export.headers.orderStatus'),
        plannedDate: t('reports:companyBalance.export.headers.plannedDate'),
        realization: t('reports:companyBalance.export.headers.realization'),
        settlementStatus: t('reports:companyBalance.export.headers.settlementStatus'),
        currentRevenue: t('reports:companyBalance.export.headers.currentRevenue'),
        currentCost: t('reports:companyBalance.export.headers.currentCost'),
        currentMargin: t('reports:companyBalance.export.headers.currentMargin'),
        currentMarginPercent: t('reports:companyBalance.export.headers.currentMarginPercent'),
        recognizedRevenue: t('reports:companyBalance.export.headers.recognizedRevenue'),
        recognizedCost: t('reports:companyBalance.export.headers.recognizedCost'),
        recognizedMargin: t('reports:companyBalance.export.headers.recognizedMargin'),
      },
      orderStatus: (status) => t(`orders:statusOptions.${status}`, {
        defaultValue: t('common:unknownStatus'),
      }),
      settlementStatus: (status) => t(`reports:companyBalance.export.settlement.${status}`),
    });
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', companyBalanceOrdersFilename(range.from, range.to));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    feedback.success(t('reports:companyBalance.export.success', { count: visibleOrders.length }));
  };

  const chartMax = useMemo(() => Math.max(
    1,
    ...(data?.months.flatMap((month) => {
      const values = chartMetricValues(month, chartMetric);
      return [Math.abs(values.recognized ?? 0), Math.abs(values.planned ?? 0)];
    }) || [1]),
  ), [chartMetric, data?.months]);

  const columns = useMemo<TableColumnsType<CompanyBalanceOrder>>(() => [
    {
      title: t('reports:companyBalance.table.order'),
      key: 'DATE',
      width: 190,
      sorter: true,
      sortOrder: orderSort.key === 'DATE' ? orderSort.direction : null,
      render: (_value, order) => (
        <span className="company-balance-order-cell">
          <Link to={`/app/orders/${order.id}?tab=finance`}>{order.displayOrderNumber}</Link>
          <small>{order.customer?.name || '—'}</small>
          <span className={`company-balance-order-cell__settlement is-${order.settlementStatus.toLowerCase()}`}>
            {t(`reports:companyBalance.orders.settlement.${order.settlementStatus}`)}
          </span>
          {order.reviewState === 'READY' && <small>{t('reports:companyBalance.readyForResult')}</small>}
          {order.reviewReasons.includes('SETTLEMENT_REQUIRES_ADMIN') && <small>{t('reports:companyBalance.resultRequiresAdmin')}</small>}
          {order.reviewReasons.includes('RESULT_REQUIRES_UPDATE') && (
            <strong role="status">{t('reports:companyBalance.resultNeedsUpdate')}</strong>
          )}
          <time>{dateLabel(order.plannedDate, language)}</time>
        </span>
      ),
    },
    {
      title: t('reports:companyBalance.table.realization'),
      key: 'REALIZATION',
      width: 100,
      align: 'center',
      sorter: true,
      sortOrder: orderSort.key === 'REALIZATION' ? orderSort.direction : null,
      render: (_value, order) => (
        <OrderRealizationValue
          order={order}
          label={t('reports:companyBalance.calendar.realization')}
        />
      ),
    },
    {
      title: (
        <FinancialColumnTitle
          label={t('reports:companyBalance.table.revenue')}
          basis={t('reports:companyBalance.table.valueBasis')}
        />
      ),
      key: 'REVENUE',
      width: 145,
      align: 'right',
      sorter: true,
      sortOrder: orderSort.key === 'REVENUE' ? orderSort.direction : null,
      render: (_value, order) => (
        <FinancialPair
          recognized={order.periodRecognized.revenue}
          forecast={order.current.revenue}
          language={language}
          recognizedLabel={t('reports:companyBalance.table.recognizedValue')}
          forecastLabel={t('reports:companyBalance.table.forecastValue')}
        />
      ),
    },
    {
      title: (
        <FinancialColumnTitle
          label={t('reports:companyBalance.table.costs')}
          basis={t('reports:companyBalance.table.valueBasis')}
        />
      ),
      key: 'COST',
      width: 145,
      align: 'right',
      sorter: true,
      sortOrder: orderSort.key === 'COST' ? orderSort.direction : null,
      render: (_value, order) => (
        <FinancialPair
          recognized={order.periodRecognized.totalCost}
          forecast={order.current.totalCost}
          language={language}
          recognizedLabel={t('reports:companyBalance.table.recognizedValue')}
          forecastLabel={t('reports:companyBalance.table.forecastValue')}
        />
      ),
    },
    {
      title: (
        <FinancialColumnTitle
          label={t('reports:companyBalance.table.margin')}
          basis={t('reports:companyBalance.table.valueBasis')}
        />
      ),
      key: 'MARGIN',
      width: 175,
      align: 'right',
      sorter: true,
      sortOrder: orderSort.key === 'MARGIN' ? orderSort.direction : null,
      render: (_value, order) => (
        <FinancialPair
          recognized={order.periodRecognized.margin}
          forecast={order.current.margin}
          language={language}
          recognizedPercent={order.periodRecognized.marginPercent}
          forecastPercent={order.current.marginPercent}
          recognizedLabel={t('reports:companyBalance.table.recognizedValue')}
          forecastLabel={t('reports:companyBalance.table.forecastValue')}
          highlight
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 42,
      render: (_value, order) => (
        <Link
          className="company-balance-order-link"
          to={`/app/orders/${order.id}?tab=finance`}
          aria-label={t('reports:companyBalance.actions.inspect')}
        >
          <ArrowRightOutlined />
        </Link>
      ),
    },
  ], [language, orderSort, t]);

  const handleTableChange: TableProps<CompanyBalanceOrder>['onChange'] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = activeSorter?.columnKey as CompanyBalanceOrderSortKey | undefined;
    if (key && activeSorter.order) {
      setOrderSort({ key, direction: activeSorter.order });
      return;
    }
    setOrderSort({ key: 'DATE', direction: 'descend' });
  };

  if (loading && !data) {
    return <div className="company-balance-loading"><Spin size="large" /></div>;
  }

  if (!data) return <Empty description={t('reports:companyBalance.empty')} />;

  const { recognized, planned, orders: ordersSummary } = data.summary;
  const currentPeriodLabel = periodLabel(anchor, period, language);

  return (
    <div className="company-balance-workspace">
      <motion.nav
        key={`${range.from}-${range.to}`}
        className={`company-balance-summary-bar ${recognized.netResult != null && recognized.netResult < 0 ? 'is-negative' : ''} ${recognized.orderCount === 0 ? 'is-empty-ledger' : ''}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        aria-label={t('reports:companyBalance.summary.ariaLabel')}
      >
        <div className="company-balance-summary-bar__result">
          <span>{t('reports:companyBalance.hero.label', { period: currentPeriodLabel })}</span>
          <strong title={formatCurrencyGrouped(recognized.netResult, 'PLN')}>
            {formatCompactPLN(recognized.netResult, language)}
          </strong>
          <small>
            {recognized.orderCount > 0
              ? t('reports:companyBalance.hero.recognizedCount', { count: recognized.orderCount })
              : t('reports:companyBalance.hero.noRecognizedOrders')}
          </small>
        </div>
        <dl>
          <div><dt>{t('reports:companyBalance.metrics.revenue')}</dt><dd>{formatCompactPLN(recognized.revenue, language)}</dd></div>
          <div><dt>{t('reports:companyBalance.metrics.costs')}</dt><dd>{formatCompactPLN(recognized.totalCost, language)}</dd></div>
          <div><dt>{t('reports:companyBalance.metrics.margin')}</dt><dd>{formatCompactPLN(recognized.margin, language)} <small>{formatLocalizedPercent(recognized.marginPercent, language)}</small></dd></div>
          <div><dt>{t('reports:companyBalance.metrics.fixedCosts')}</dt><dd>{formatCompactPLN(recognized.fixedCosts, language)}</dd></div>
        </dl>
      </motion.nav>

      <header className="company-balance-toolbar">
        <div>
          <span>{t('reports:companyBalance.period')}</span>
          <DatePicker
            value={anchor}
            onChange={(value) => value && setAnchor(value)}
            picker={period === 'YEAR' ? 'year' : period === 'QUARTER' ? 'quarter' : 'month'}
            allowClear={false}
          />
        </div>
        <Segmented<CompanyBalancePeriod>
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'MONTH', label: t('reports:companyBalance.periods.month') },
            { value: 'QUARTER', label: t('reports:companyBalance.periods.quarter') },
            { value: 'HALF_YEAR', label: t('reports:companyBalance.periods.halfYear') },
            { value: 'YEAR', label: t('reports:companyBalance.periods.year') },
          ]}
        />
        <small>{t('reports:companyBalance.freshness')}</small>
      </header>

      <section className="company-balance-story">
        <div className="company-balance-trend">
          <header>
            <div>
              <h2>{t('reports:companyBalance.trend.title')}</h2>
              <p>{t('reports:companyBalance.trend.description')}</p>
            </div>
            <div className="company-balance-chart-controls">
              <span>{t('reports:companyBalance.trend.metric')}</span>
              <Segmented<CompanyBalanceChartMetric>
                value={chartMetric}
                onChange={setChartMetric}
                options={[
                  { value: 'REVENUE', label: t('reports:companyBalance.metrics.revenue') },
                  { value: 'COSTS', label: t('reports:companyBalance.metrics.costs') },
                  { value: 'MARGIN', label: t('reports:companyBalance.metrics.margin') },
                ]}
              />
              <span className="company-balance-legend">
                <i className="is-recognized" />{t('reports:companyBalance.trend.recognizedShort')}
                <i className="is-planned" />{t('reports:companyBalance.trend.plannedShort')}
              </span>
            </div>
          </header>
          <div className="company-balance-trend__plan-summary">
            <span>{t('reports:companyBalance.trend.planSummary', { count: planned.orderCount })}</span>
            <dl>
              <div><dt>{t('reports:companyBalance.metrics.revenue')}</dt><dd>{formatCompactPLN(planned.revenue, language)}</dd></div>
              <div><dt>{t('reports:companyBalance.metrics.costs')}</dt><dd>{formatCompactPLN(planned.totalCost, language)}</dd></div>
              <div><dt>{t('reports:companyBalance.metrics.margin')}</dt><dd>{formatCompactPLN(planned.margin, language)}</dd></div>
              <div><dt>{t('reports:companyBalance.metrics.plannedFixedCosts')}</dt><dd>{formatCompactPLN(planned.fixedCosts, language)}</dd></div>
            </dl>
            {planned.reviewOrderCount > 0 && (
              <em><ExclamationCircleOutlined /> {t('reports:companyBalance.hero.reviewCount', { count: planned.reviewOrderCount })}</em>
            )}
          </div>
          <div
            className={`company-balance-trend__plot ${chartMetric === 'MARGIN' ? 'is-signed' : ''}`}
            style={{ minWidth: Math.max(680, data.months.length * 82) }}
          >
            {data.months.map((month, index) => {
              const metricValues = chartMetricValues(month, chartMetric);
              const signed = chartMetric === 'MARGIN';
              const heightScale = signed ? 38 : 76;
              const recognizedHeight = metricValues.recognized == null || metricValues.recognized === 0
                ? 0
                : Math.max(3, (Math.abs(metricValues.recognized) / chartMax) * heightScale);
              const plannedHeight = metricValues.planned == null || metricValues.planned === 0
                ? 0
                : Math.max(3, (Math.abs(metricValues.planned) / chartMax) * heightScale);
              const combined = {
                revenue: month.revenue + month.planned.revenue,
                totalCost: nullableSum(month.orderCosts, month.planned.totalCost),
                margin: nullableSum(month.orderMargin, month.planned.margin),
              };
              return (
                <Tooltip
                  key={month.month}
                  placement="top"
                  mouseEnterDelay={0.08}
                  overlayClassName="company-balance-chart-tooltip-overlay"
                  title={(
                    <div className="company-balance-chart-tooltip">
                      <strong>{monthLabel(month.month, language)}</strong>
                      <div className="company-balance-chart-tooltip__head">
                        <span />
                        <span>{t('reports:companyBalance.metrics.revenue')}</span>
                        <span>{t('reports:companyBalance.metrics.costs')}</span>
                        <span>{t('reports:companyBalance.metrics.margin')}</span>
                      </div>
                      <div><span>{t('reports:companyBalance.trend.recognizedShort')}</span><b>{formatCompactPLN(month.revenue, language)}</b><b>{formatCompactPLN(month.orderCosts, language)}</b><b>{formatCompactPLN(month.orderMargin, language)}</b></div>
                      <div><span>{t('reports:companyBalance.trend.plannedShort')}</span><b>{formatCompactPLN(month.planned.revenue, language)}</b><b>{formatCompactPLN(month.planned.totalCost, language)}</b><b>{formatCompactPLN(month.planned.margin, language)}</b></div>
                      <div className="is-total"><span>{t('reports:companyBalance.trend.total')}</span><b>{formatCompactPLN(combined.revenue, language)}</b><b>{formatCompactPLN(combined.totalCost, language)}</b><b>{formatCompactPLN(combined.margin, language)}</b></div>
                    </div>
                  )}
                >
                  <button
                    type="button"
                    className="company-balance-month"
                    onClick={() => {
                      setAnchor(dayjs(`${month.month}-01`));
                      setPeriod('MONTH');
                    }}
                    aria-label={t('reports:companyBalance.trend.openMonth', { month: monthLabel(month.month, language) })}
                  >
                    <span className="company-balance-month__bars">
                      <span className="company-balance-month__bar-slot">
                        <motion.i
                          className={`is-recognized ${metricValues.recognized != null && metricValues.recognized < 0 ? 'is-negative' : ''}`}
                          initial={{ height: 0 }}
                          animate={{ height: `${recognizedHeight}%` }}
                          transition={{ duration: 0.3, delay: index * 0.02 }}
                          style={signed
                            ? metricValues.recognized != null && metricValues.recognized < 0 ? { top: '50%' } : { bottom: '50%' }
                            : { bottom: 0 }}
                        >
                          <b>{formatCompactPLN(metricValues.recognized, language)}</b>
                        </motion.i>
                      </span>
                      <span className="company-balance-month__bar-slot">
                        <motion.i
                          className={`is-planned ${metricValues.planned != null && metricValues.planned < 0 ? 'is-negative' : ''}`}
                          initial={{ height: 0 }}
                          animate={{ height: `${plannedHeight}%` }}
                          transition={{ duration: 0.3, delay: 0.05 + index * 0.02 }}
                          style={signed
                            ? metricValues.planned != null && metricValues.planned < 0 ? { top: '50%' } : { bottom: '50%' }
                            : { bottom: 0 }}
                        >
                          <b>{formatCompactPLN(metricValues.planned, language)}</b>
                        </motion.i>
                      </span>
                    </span>
                    <strong>{monthLabel(month.month, language)}</strong>
                    <small className={(nullableSum(metricValues.recognized, metricValues.planned) ?? 0) < 0 ? 'is-negative' : ''}>
                      <span>{t('reports:companyBalance.trend.totalShort')}</span>
                      {formatCompactPLN(nullableSum(metricValues.recognized, metricValues.planned), language)}
                    </small>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>

      </section>

      <section className="company-balance-orders">
        <header>
          <div>
            <h2>{t('reports:companyBalance.orders.title')}</h2>
            <p>{t('reports:companyBalance.orders.description')}</p>
          </div>
          <Tooltip title={t('reports:companyBalance.export.hint')}>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadVisibleOrders}
              disabled={visibleOrders.length === 0}
            >
              {t('reports:companyBalance.export.download')}
            </Button>
          </Tooltip>
        </header>
        <button
          type="button"
          className="company-balance-orders-summary"
          onClick={() => setOrdersExpanded((expanded) => !expanded)}
          aria-expanded={ordersExpanded}
          aria-controls="company-balance-orders-drilldown"
        >
          <span className="company-balance-orders-summary__metric is-count">
            <small>{t('reports:companyBalance.orders.summary.orderCount')}</small>
            <strong>{ordersSummary.orderCount}</strong>
          </span>
          <span className="company-balance-orders-summary__metric is-realization">
            <small>{t('reports:companyBalance.orders.summary.averageRealization')}</small>
            <strong>
              {ordersSummary.averageRealizationPercent === null
                ? '—'
                : formatLocalizedPercent(ordersSummary.averageRealizationPercent, language)}
            </strong>
            <i aria-hidden="true">
              <b style={{ width: `${ordersSummary.averageRealizationPercent ?? 0}%` }} />
            </i>
          </span>
          <span className="company-balance-orders-summary__metric">
            <small>{t('reports:companyBalance.orders.summary.revenue')}</small>
            <strong title={formatCurrencyGrouped(ordersSummary.revenue, 'PLN')}>
              {formatCompactPLN(ordersSummary.revenue, language)}
            </strong>
          </span>
          <span className="company-balance-orders-summary__metric">
            <small>{t('reports:companyBalance.orders.summary.costs')}</small>
            <strong title={formatCurrencyGrouped(ordersSummary.totalCost, 'PLN')}>
              {formatCompactPLN(ordersSummary.totalCost, language)}
            </strong>
          </span>
          <span className="company-balance-orders-summary__metric is-margin">
            <small>{t('reports:companyBalance.orders.summary.averageMargin')}</small>
            <strong>{formatLocalizedPercent(ordersSummary.marginPercent, language)}</strong>
          </span>
          <span className="company-balance-orders-summary__action">
            {ordersExpanded
              ? t('reports:companyBalance.orders.summary.hideOrders')
              : t('reports:companyBalance.orders.summary.showOrders')}
            {ordersExpanded ? <UpOutlined /> : <DownOutlined />}
          </span>
        </button>
        {ordersExpanded && (
          <motion.div
            id="company-balance-orders-drilldown"
            className="company-balance-orders__drilldown"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="company-balance-orders__filters">
              <Input
                prefix={<SearchOutlined />}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
                placeholder={t('reports:companyBalance.filters.search')}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
                placeholder={t('reports:companyBalance.filters.status')}
                options={availableStatuses.map((status) => ({
                  value: status,
                  label: t(`orders:statusOptions.${status}`, { defaultValue: t('common:unknownStatus') }),
                }))}
              />
              <span>{t('reports:companyBalance.filters.count', { count: visibleOrders.length })}</span>
            </div>
            <Table<CompanyBalanceOrder>
              rowKey="id"
              size="small"
              dataSource={visibleOrders}
              columns={columns}
              loading={loading}
              onChange={handleTableChange}
              pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['15', '30', '60'] }}
              tableLayout="fixed"
              rowClassName={() => 'company-balance-row'}
              locale={{ emptyText: t('reports:companyBalance.orders.empty') }}
            />
          </motion.div>
        )}
      </section>
    </div>
  );
};

export default CompanyBalanceWorkspace;
